import type { TaskFilterType, TaskLikeObject } from '@frontend/api-client';
import {
  groupTasksBySimplifiedStatus,
  isTaskInProgress,
  isTaskMarkedAsFailedToComplete,
  isTaskMarkedAsSuccessfullyCompleted,
  isTaskRepeating,
  isTaskSnoozed,
  isTaskStartingSoon,
} from '@frontend/api-client';
import type { DraftTask, Task } from '@frontend/api-types';
import { P, filterNullish, match } from '@frontend/duck-tape';

export const ALL_TASK_TAB_OBJECTS = [
  { key: 'inProgress', title: 'In Progress' },
  { key: 'repeating', title: 'Repeating' },
  { key: 'startingSoon', title: 'Starting Soon' },
  { key: 'snoozed', title: 'Snoozed' },
  { key: 'draft', title: 'Drafts' },
  { key: 'done', title: 'Done' },
  { key: 'closedIncomplete', title: 'Closed' },
] as const satisfies { key: TaskFilterType; title: string }[];

export type TaskTabObject = (typeof ALL_TASK_TAB_OBJECTS)[number];

export const getVisibleTaskFilters = (
  tasks: Task[],
  draftTasks: DraftTask[],
  markAsDoneDeployDate: MaybeNull<DateISOString>,
): TaskTabObject[] => {
  const simplifiedTasks = groupTasksBySimplifiedStatus(tasks, markAsDoneDeployDate);

  const hasInProgressTasks = !!simplifiedTasks.inProgress.length;
  const hasStartingSoonTasks = !!simplifiedTasks.startingSoon.length;
  const hasDoneTasks = Boolean(simplifiedTasks.doneLast24Hours.length || simplifiedTasks.doneOver24Hours.length);
  const hasDraftTasks = !!draftTasks.length;
  const hasClosedIncompleteTasks = !!simplifiedTasks.failedToComplete.length;
  const hasRepeatingTasks = tasks.some(isTaskRepeating);
  const hasSnoozedTasks = tasks.some(isTaskSnoozed);

  return filterNullish([
    hasInProgressTasks ? { key: 'inProgress', title: 'In Progress' } : null,
    hasRepeatingTasks ? { key: 'repeating', title: 'Repeating' } : null,
    hasStartingSoonTasks ? { key: 'startingSoon', title: 'Starting Soon' } : null,
    hasSnoozedTasks ? { key: 'snoozed', title: 'Snoozed' } : null,
    hasDraftTasks ? { key: 'draft', title: 'Drafts' } : null,
    hasDoneTasks ? { key: 'done', title: 'Done' } : null,
    hasClosedIncompleteTasks ? { key: 'closedIncomplete', title: 'Closed' } : null,
  ]);
};

const getTaskPredicates = (taskFilters: TaskFilterType[], markAsDoneDeployDate: MaybeNull<DateISOString>) =>
  taskFilters.map((filter) =>
    match<typeof filter, (task: Task) => boolean>(filter)
      .with('startingSoon', () => isTaskStartingSoon)
      .with('inProgress', () => isTaskInProgress)
      .with('done', () => (task) => isTaskMarkedAsSuccessfullyCompleted(task, markAsDoneDeployDate))
      .with('closedIncomplete', () => isTaskMarkedAsFailedToComplete)
      .with('repeating', () => isTaskRepeating)
      .with('snoozed', () => isTaskSnoozed)
      // We don't handle this case
      .with('draft', () => (_) => false)
      .with(P.nullish, () => (_) => true)
      .exhaustive(),
  );

export const filterTaskLikeObjectsByTabType = ({
  markAsDoneDeployDate,
  taskFilters,
  taskLikeObjects,
}: {
  markAsDoneDeployDate: MaybeNull<DateISOString>;
  taskFilters: TaskFilterType[];
  taskLikeObjects: TaskLikeObject[];
}) => {
  if (taskFilters.length === 0) {
    return taskLikeObjects.filter((obj) => (obj.__typename === 'Task' ? !isTaskSnoozed(obj) : true));
  }

  const taskPredicates = getTaskPredicates(taskFilters, markAsDoneDeployDate);

  return taskLikeObjects.filter((taskLikeObject) => {
    return match(taskLikeObject)
      .with({ __typename: 'Task' }, (task) => {
        // @ts-expect-error Quirk with the types
        return taskPredicates.some((predicate) => predicate(task));
      })
      .with({ __typename: 'DraftTask' }, () => taskFilters.includes('draft'))
      .exhaustive();
  });
};

export const joinTaskLikeObjects = ({
  draftTasks,
  tasks,
}: {
  draftTasks: DraftTask[];
  tasks: Task[];
}): TaskLikeObject[] => [
  ...tasks.map((task) => ({ ...task, __typename: 'Task' as const })),
  ...draftTasks.map((draftTask) => ({ ...draftTask, __typename: 'DraftTask' as const })),
];

type HandleSelectFilterParams = {
  activeFilters: TaskFilterType[];
  selectedFilter: TaskFilterType;
};

export const handleSelectFilter = ({ activeFilters, selectedFilter }: HandleSelectFilterParams) => {
  const handleNormalSelection = ({ activeFilters, selectedFilter }: HandleSelectFilterParams) => {
    const isFilterAlreadyActive = activeFilters.includes(selectedFilter);
    return isFilterAlreadyActive
      ? activeFilters.filter((filter) => filter !== selectedFilter)
      : [...activeFilters, selectedFilter];
  };

  const handleDeselectOtherFilters = ({
    activeFilters,
    autoExcludeKeys,
    selectedFilter,
  }: HandleSelectFilterParams & { autoExcludeKeys: TaskFilterType[] }) => {
    const newActiveFilters = handleNormalSelection({ activeFilters, selectedFilter });
    return newActiveFilters.filter((filter) => !autoExcludeKeys.includes(filter));
  };

  if (selectedFilter === 'repeating') {
    return handleDeselectOtherFilters({
      activeFilters,
      autoExcludeKeys: ['startingSoon', 'inProgress', 'draft', 'closedIncomplete', 'done'],
      selectedFilter,
    });
  }

  if (activeFilters.includes('repeating')) {
    return handleDeselectOtherFilters({
      activeFilters,
      autoExcludeKeys: ['repeating'],
      selectedFilter,
    });
  }

  return handleNormalSelection({ activeFilters, selectedFilter });
};
